import { Card, CardContent, CardHeader, CardTitle } from '@city-sage/aurum-avis-labs-components';

export default function PrivacyPage() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl font-bold">Privacy Statement</CardTitle>
          <p className="text-muted-foreground">**Last Updated: 01.11.2024**</p>
        </CardHeader>
        <CardContent className="prose prose-gray max-w-none">
          <p>
            Aurum Avis Labs GmbH ("CitySage," "we," "us," or "our") is committed to protecting your privacy. This Privacy Statement explains how we collect, use, share, and protect your personal information when you use the CitySage app.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">1. **Data Collection**</h3>
          <p>
            In compliance with the <strong>Swiss Federal Act on Data Protection (FADP)</strong> and <strong>EU GDPR Articles 5, 6, and 13</strong>, CitySage collects and processes personal data as follows:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Information</strong>: When you create an account, we collect your email address, username, and any optional profile information you provide.</li>
            <li><strong>Location Data</strong>: To enhance the app's navigation and tour features, CitySage may collect real-time location data when you enable this feature.</li>
            <li><strong>Payment Information</strong>: When you purchase premium tours, we collect payment information through a secure third-party payment processor, who handles and stores payment data in compliance with PCI-DSS standards.</li>
            <li><strong>Usage Data</strong>: We collect data on how you interact with the App, including device type, IP address, and browsing behavior, to improve functionality and user experience.</li>
          </ul>

          <h3 className="text-xl font-semibold mt-8 mb-4">2. **Purpose of Data Collection**</h3>
          <p>
            CitySage collects personal data for the following purposes:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service Provision</strong>: To provide, personalize, and enhance your experience with CitySage.</li>
            <li><strong>Payment Processing</strong>: To facilitate secure transactions for one-time purchases of premium tours.</li>
            <li><strong>Analytics and Improvements</strong>: To analyze usage patterns and improve the App's functionality.</li>
            <li><strong>Marketing</strong>: With your consent, we may send promotional content or offer updates via email. You can opt-out at any time.</li>
          </ul>
          <p>
            Data collection and processing are based on <strong>Art. 6(1)(b) GDPR</strong> for contract performance, <strong>Art. 6(1)(a) GDPR</strong> for consent-based activities, and <strong>Art. 31 FADP</strong> for legitimate interest in Switzerland.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">3. **Third-Party Sharing**</h3>
          <p>
            CitySage may share your personal data with third-party service providers, as necessary:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Payment Processors</strong>: For secure processing of one-time purchases.</li>
            <li><strong>Analytics Providers</strong>: To help analyze usage data and improve the App. Data shared with these providers is anonymized where possible.</li>
            <li><strong>Legal Compliance</strong>: We may disclose information to comply with legal obligations or respond to lawful requests from public authorities, in line with <strong>Art. 6(1)(c) GDPR</strong> and <strong>Art. 13 FADP</strong>.</li>
          </ul>
          <p>
            We require all third-party providers to adhere to strict data protection standards as required by the FADP and GDPR.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">4. **Data Storage and Security**</h3>
          <p>
            CitySage stores user data on secure servers located in Switzerland and the EU. We implement appropriate technical and organizational measures, such as encryption and access controls, to protect your data from unauthorized access, loss, or misuse, in compliance with <strong>Art. 7 FADP</strong> and <strong>Art. 32 GDPR</strong>.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">5. **User Rights**</h3>
          <p>
            Under the <strong>FADP</strong> and <strong>GDPR Articles 15-22</strong>, users have the following rights regarding their personal data:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Access</strong>: You can request to know what personal data we hold about you.</li>
            <li><strong>Rectification</strong>: You can request corrections to inaccurate or incomplete data.</li>
            <li><strong>Deletion</strong>: You can request the deletion of your personal data, subject to legal retention requirements.</li>
            <li><strong>Restriction</strong>: You can request a restriction on processing your data under certain conditions.</li>
            <li><strong>Objection</strong>: You can object to processing based on legitimate interests or for direct marketing purposes.</li>
            <li><strong>Data Portability</strong>: You have the right to receive your data in a structured, commonly used, and machine-readable format.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at [Insert Contact Email]. We may require verification of your identity before processing requests.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">6. **Cookies and Tracking Technologies**</h3>
          <p>
            CitySage uses cookies and similar tracking technologies to provide a seamless experience and analyze user interaction with the App. We use:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Necessary Cookies</strong>: Essential for the operation of the App.</li>
            <li><strong>Analytical Cookies</strong>: To gather data on app usage and improve functionality.</li>
          </ul>
          <p>
            Users in the EU are provided with a consent banner upon their first visit, in compliance with <strong>Art. 6(1)(f) GDPR</strong> and <strong>Swiss FADP</strong>. You may manage your cookie preferences at any time in your account settings.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">7. **Cross-Border Data Transfers**</h3>
          <p>
            When transferring data outside Switzerland or the EU, CitySage uses appropriate safeguards, such as <strong>Standard Contractual Clauses (SCCs)</strong>, to protect your data in compliance with <strong>Art. 46 GDPR</strong> and <strong>Art. 6(2)(a) FADP</strong>. These transfers are primarily conducted within secure servers hosted in countries with adequate data protection standards.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">8. **Data Retention**</h3>
          <p>
            CitySage retains personal data only as long as necessary to fulfill the purposes outlined in this Privacy Statement or as required by law. Generally:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account Information</strong> is retained for as long as your account is active.</li>
            <li><strong>Location and Usage Data</strong> is anonymized or deleted after it is no longer needed for operational purposes.</li>
            <li><strong>Payment Information</strong> is retained only for the duration required by financial regulations and then deleted or anonymized.</li>
          </ul>

          <h3 className="text-xl font-semibold mt-8 mb-4">9. **Changes to This Privacy Statement**</h3>
          <p>
            We may update this Privacy Statement to reflect changes in our data practices or applicable laws. You will be notified of any material changes, and continued use of the App following such changes constitutes acceptance of the new Privacy Statement.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">10. **Contact Information**</h3>
          <p>
            If you have questions about this Privacy Statement or wish to exercise your data rights, please contact us at:
          </p>
          <div className="bg-muted p-4 rounded-lg">
            <p className="font-semibold">**Aurum Avis Labs GmbH**</p>
            <p>info@aurum-avis-labs.ch</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 